import { useEffect, useRef, useLayoutEffect } from "react";
import * as echarts from "echarts";
import moment from "moment";
import "moment-timezone";

import MeasureColors from "../ColorsDataSheet";
import { AlertsChartsParams } from "site-apps/alerts/AlertsTypesCharts";
import { useTranslation } from "react-i18next";

const LineEChartMultiple = ({ measuresData, translations, options, alerts, chartRef, min = 0, max = 100 }) => {
    const chartContainer = useRef(null);
    const chartInstance = useRef(null);
    const { t: _Tc } = useTranslation("choices");

    useEffect(() => {
        // Inicializar EChart
        if (!chartInstance.current && chartContainer.current) {
            // Dimensiones del Padre
            chartInstance.current = echarts.init(chartContainer.current, null, {
                renderer: "canvas",
                useDirtyRect: false,
            });

            // Reset Zoom 0 a 100% data
            chartInstance.current.resetZoom = function () {
                chartInstance.current.dispatchAction({
                    type: "dataZoom",
                    start: 0,
                    end: 100,
                });
            };

            // Expose chart instance to parent component if chartRef is provided
            if (chartRef) {
                chartRef(chartInstance.current);
            }
        }
    }, [chartRef]);

    // Force Resize
    useLayoutEffect(() => {
        if (chartInstance.current) {
            setTimeout(() => {
                chartInstance.current.resize();
            }, 100);
        }
    });

    useEffect(() => {
        if (chartInstance.current && measuresData && Object.keys(measuresData).length > 0) {
            const series = [];
            const legendData = [];

            // Para cada tipo de medida
            Object.keys(measuresData).forEach((measureType) => {
                const measures = measuresData[measureType];
                if (!measures || measures.length === 0) return;

                const name = translations?.[measureType] || measureType;
                legendData.push(name);

                const colors = MeasureColors.getColor(measureType);

                const data = measures.map((item) => [
                    new Date(item[0] * 1000), // Convert timestamp to Date object
                    Math.min(item[1], 100), // Y value (limited to max 100)
                ]);

                // Crear la serie para este tipo de medida
                series.push({
                    name: name,
                    type: "line",
                    showSymbol: true,
                    symbol: "circle",
                    symbolSize: 4,
                    smooth: false,
                    itemStyle: {
                        color: colors.borderColor,
                    },
                    data: data,
                });
            });

            if (alerts) {
                Object.keys(alerts).forEach((alertType) => {
                    if (alerts[alertType] && alerts[alertType].length > 0) {
                        // Color por tipo de alerta
                        const alertColor = AlertsChartsParams.getAlertColor(alertType);

                        // Create alert series
                        const alertData = alerts[alertType].map((alert) => [
                            new Date(alert.x), // Convert timestamp to Date object
                            Math.min(alert.y, 100), // Y value (limited to max 100)
                        ]);

                        // Add to legend data
                        let alertName = _Tc(AlertsChartsParams.getTitle(alertType));
                        legendData.push(alertName);

                        // Add alert series
                        series.push({
                            name: alertName,
                            type: "scatter",
                            symbol: AlertsChartsParams.getIcon(alertType),
                            symbolSize: 12,
                            itemStyle: {
                                color: alertColor,
                            },
                            data: alertData,
                            z: 10, // Make alerts appear above other elements
                        });
                    }
                });
            }

            // Create ECharts option
            const echartsOption = {
                tooltip: {
                    trigger: "axis",
                    formatter: function (params) {
                        let result = "";
                        if (params[0] && params[0].axisValue) {
                            // Format date in tooltip
                            result = moment(params[0].axisValue).format("DD-MM-YYYY HH:mm:ss") + "<br>";
                        }
                        // Add each series data
                        params.forEach((param) => {
                            result += param.marker + " " + param.seriesName + ": " + param.value[1] + "<br/>";
                        });
                        return result;
                    },
                },
                legend: {
                    data: legendData,
                    top: "top",
                },
                grid: {
                    left: "17px",
                    right: "65px",
                    bottom: "10px",
                    containLabel: true,
                },
                toolbox: {
                    feature: {
                        restore: {},
                        saveAsImage: {},
                    },
                },
                xAxis: {
                    type: "time",
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: "#d8d8d8",
                            width: 1,
                            type: "dashed",
                        },
                    },
                    boundaryGap: false,
                    axisLabel: {
                        formatter: "{dd}-{MM}-{yyyy} {HH}:{mm}:{ss}",
                        hideOverlap: true,
                        padding: [6, 0, 0, 0],
                        margin: 20,
                    },
                },
                yAxis: {
                    type: "value",
                    boundaryGap: [0, "10%"],
                    min: min,
                    max: max,
                },
                dataZoom: [
                    // Zoom interno con la rueda del ratón
                    {
                        type: "inside",
                        start: 0,
                        end: 100,
                        zoomOnMouseWheel: true,
                    },
                    // Slider en la parte inferior
                    {
                        type: "slider",
                        start: 0,
                        end: 100,
                        bottom: 10,
                        height: 20,
                        showDetail: false,
                    },
                ],
                series: series,
            };

            let mergedOptions = echartsOption;

            if (options) {
                mergedOptions = { ...echartsOption, ...options };

                // Asegurarnos de que siempre se mantenga el rango de 0 a 100 en el eje Y
                if (!mergedOptions.yAxis) {
                    mergedOptions.yAxis = {};
                }
                mergedOptions.yAxis.min = 0;
                mergedOptions.yAxis.max = 100;

                // Solo botones específicos en la toolbox
                if (options.toolbox && options.toolbox.feature) {
                    mergedOptions.toolbox = {
                        ...options.toolbox,
                        feature: {
                            restore: options.toolbox.feature.restore || {},
                            saveAsImage: options.toolbox.feature.saveAsImage || {},
                        },
                    };
                }
            }

            chartInstance.current.setOption(mergedOptions, true);

            // Reset zoom to show all data
            setTimeout(() => {
                chartInstance.current.resize();
            }, 100);
        }
    }, [measuresData, translations, options, alerts]);

    return (
        <div
            ref={chartContainer}
            style={{
                width: "100%",
                height: "300px",
                maxWidth: "100%",
                overflow: "hidden",
            }}
        />
    );
};

export default LineEChartMultiple;
